import React from 'react';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { customerGlobalSearchQuery } from '../../../../../../store/customer/selector';
import { CustomerGlobalSearchResponseNormalized } from '../../../../../../store/customer/types';

interface UseStepOneCustomerSearchProps {
  searchTerm: string;
  hasSelectedCustomer: boolean;
}

interface UseStepOneCustomerSearchResult {
  loadingCustomersSearch: boolean;
  customerSearchResultItems: CustomerGlobalSearchResponseNormalized[];
  showNotFoundMessage: boolean;
  canShowSearchResults: boolean;
}

export const useStepOneCustomerSearch = ({
  searchTerm,
  hasSelectedCustomer,
}: UseStepOneCustomerSearchProps): UseStepOneCustomerSearchResult => {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
  const normalizedSearchTerm = searchTerm.trim();
  const isDebouncingSearchTerm = normalizedSearchTerm !== debouncedSearchTerm;
  const hasMinSearchLength = normalizedSearchTerm.length >= 2;
  const canShowSearchResults =
    !hasSelectedCustomer && hasMinSearchLength && !isDebouncingSearchTerm;

  const { data: customersSearchData, loading: loadingCustomersSearch } = useRecoilQuery(
    customerGlobalSearchQuery({
      searchTerm: debouncedSearchTerm,
    }),
    {
      if: () => debouncedSearchTerm.length >= 2,
      cachePolicy: 'no-cache',
    }
  );

  const customerSearchResultItems = React.useMemo(
    () => customersSearchData?.data || [],
    [customersSearchData?.data]
  );

  const showNotFoundMessage =
    canShowSearchResults && !loadingCustomersSearch && !customerSearchResultItems.length;

  React.useEffect(() => {
    if (hasSelectedCustomer) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(normalizedSearchTerm);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [hasSelectedCustomer, normalizedSearchTerm]);

  return {
    loadingCustomersSearch,
    customerSearchResultItems,
    showNotFoundMessage,
    canShowSearchResults,
  };
};
